// OBT — Müdür paneli (F1 iskeleti + F2-A kampanya entegrasyonu)
import { useState } from 'react';
import {
  ClipboardList,
  Gavel,
  Clock,
  Megaphone,
  Search,
  BarChart3,
  Archive,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import ObtDashboardFrame from '../ObtDashboardFrame';
import { useObtOverview, sumCampaignCounter } from '../useObtOverview';
import CampaignScreen from '../campaign/CampaignScreen';
import DirectorFinalDecisionScreen from '../review/DirectorFinalDecisionScreen';
import ObtReportsScreen from '../report/ObtReportsScreen';
import ObtMemoryScreen from '../memory/ObtMemoryScreen';
import ObtNotificationsPanel from '../notifications/ObtNotificationsPanel';
import ObtNotificationBell from '../notifications/ObtNotificationBell';

const TASK_CARDS = [
  {
    key: 'decisions',
    title: 'Nihai Kararlar',
    description: 'Karar setini oluşturun, park seçimlerini kesinleştirin.',
    icon: Gavel,
  },
  {
    key: 'reports',
    title: 'Raporlama',
    description: 'Bilgi talebi, park ve personel bazlı analizler; cevap oranı ve süreler.',
    icon: BarChart3,
  },
  {
    key: 'memory',
    title: 'Kurumsal Hafıza',
    description: 'Park varlıklarının yapım/değişim geçmişi, firma, ihale ve garanti kayıtları.',
    icon: Archive,
  },
];

export default function ObtDirectorDashboard({ enabled = true }) {
  const [view, setView] = useState('overview');
  const { loading, error, campaigns, page, queueItems, reload } = useObtOverview(
    'director',
    { enabled: enabled && view === 'overview' }
  );

  if (view === 'campaigns') {
    return (
      <CampaignScreen enabled={enabled} onBack={() => setView('overview')} />
    );
  }

  if (view === 'decisions') {
    return (
      <DirectorFinalDecisionScreen
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'reports') {
    return (
      <ObtReportsScreen
        role="director"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'memory') {
    return (
      <ObtMemoryScreen
        role="director"
        enabled={enabled}
        onBack={() => setView('overview')}
      />
    );
  }

  if (view === 'notifications') {
    return (
      <ObtNotificationsPanel
        role="director"
        enabled={enabled}
        onNavigate={(v) => setView(v)}
        onBack={() => setView('overview')}
      />
    );
  }

  const stats = [
    {
      key: 'queue',
      label: 'Kararımı bekleyen cevap',
      value: queueItems ? queueItems.length : 0,
      icon: Gavel,
      tone: 'warning',
      onClick: () => setView('decisions'),
    },
    {
      key: 'total',
      label: 'Kapsamımdaki bilgi talebi',
      value: page?.total ?? campaigns.length,
      icon: ClipboardList,
      tone: 'info',
      onClick: () => setView('campaigns'),
    },
    {
      key: 'pending-director',
      label: 'Müdür incelemesinde',
      value: sumCampaignCounter(campaigns, 'pending_director_count'),
      icon: Search,
      tone: 'warning',
      onClick: () => setView('decisions'),
    },
    {
      key: 'late',
      label: 'Süresi geçen kişi',
      value: sumCampaignCounter(campaigns, 'late_person_count'),
      icon: Clock,
      tone: 'danger',
      onClick: () => setView('reports'),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-end gap-2">
        <ObtNotificationBell
          enabled={enabled && view === 'overview'}
          onClick={() => setView('notifications')}
        />
        <Button variant="outline" onClick={() => setView('reports')}>
          <BarChart3 className="mr-2 h-4 w-4" aria-hidden="true" />
          Raporlar
        </Button>
        <Button variant="outline" onClick={() => setView('memory')}>
          <Archive className="mr-2 h-4 w-4" aria-hidden="true" />
          Kurumsal Hafıza
        </Button>
        <Button variant="outline" onClick={() => setView('decisions')}>
          <Gavel className="mr-2 h-4 w-4" aria-hidden="true" />
          Nihai Kararlar
        </Button>
        <Button onClick={() => setView('campaigns')}>
          <Megaphone className="mr-2 h-4 w-4" aria-hidden="true" />
          Bilgi Taleplerini Yönet
        </Button>
      </div>
      <ObtDashboardFrame
        title="Operasyonel Bilgi Toplama"
        description="Saha bilgilerinin dinamik bilgi talepleriyle toplanması, incelenmesi ve karara bağlanması."
        stats={stats}
        taskCards={TASK_CARDS}
        campaignsTitle="Son Bilgi Talepleri"
        campaignsEmptyText="Henüz bilgi talebi bulunmuyor"
        campaigns={campaigns}
        loading={loading}
        error={error}
        onReload={reload}
        onCampaignClick={() => setView('campaigns')}
      />
    </div>
  );
}
